/**
 * UserCouponListPresenter
 * --
 */
import React from 'react';
import {
  Content,
  Title,
  Row,
  Col,
  ExpandableContainer,
  Statistic,
  ModalLayout,
  CouponList,
  Text,
} from 'components';
import {
  Avatar,
  Button,
  DatePicker,
  Radio,
  Table,
  Descriptions as AntDescriptions,
  Spin,
  Tag,
  Flex,
  Select,
  Input,
} from 'antd';
import { Link } from 'react-router-dom';
import { encrypt } from 'utils/Crypto';
import MessageAlert, { strCut, stringToMoneyFormat } from 'utils';
import { LoadingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { downloadXlsx } from 'utils/XlsxDownloader';
import Formatter from 'utils/Formatter';
import { handleSearchKeyword } from 'utils/SearchKeyword';

const SUMMARY_VALUE = {
  coupon_count: 0,
  used_coupon_count: 0,
  not_used_coupon_count: 0,
  expired_coupon_count: 0,
  expired_7ago_coupon_count: 0,
};

const ACCOUNT_STATUS = {
  ACTIVE: { label: '정상', color: 'green' },
  INACTIVE: { label: '비활성', color: 'orange' },
  DORMANT: { label: '휴면', color: 'grey' },
  WITHDRAWN: { label: '탈퇴', color: 'red' },
};

const TYPE_VAlUE = {
  //회원구분
  USER: {
    label: '회원',
    color: 'orange',
  },
  NONE: { label: '비회원', color: 'default' },
  //사업자구분
  0: {
    label: '일반회원',
    color: 'default',
  },
  1: {
    label: '검수중',
    color: 'red',
  },
  2: {
    label: '사업자회원',
    color: 'green',
  },
  //가입경로구분
  MEMBER: { label: '일반가입', color: 'processing' },
  NAVER: { label: '네이버', color: 'var(--naver-color)' },
  GOOGLE: { label: '구글', color: '#F2F2F2' },
  APPLE: { label: '애플', color: '#000000' },
  KAKAO: { label: '카카오', color: 'var(--kakao-color)' },

  W: { label: '원' },
  R: { label: '%' },
};
const VOUCHER_TYPE = {
  AMOUNT_DISCOUNT: {
    label: '금액할인권',
    color: 'default',
  },
  PERCENT_DISCOUNT: {
    label: '% 할인권',
    color: 'default',
  },
  EXCHANGE: {
    label: '교환권',
    color: 'default',
  },
};
const EXPIRY = 7;
const EXPIRY_DATE = dayjs().startOf('day').unix();
/**----------------------------------------
 * [Component] 총관리자: 회원관리 -> 쿠폰발급내역
 * ----------------------------------------
 */
const UserCouponListPresenter = ({
  couponList,
  couponUserList,
  coupon,
  permission,
  loading,
  currentCoupon,
  setCurrentCoupon,
  setPeriod,
  onChange,
  inputValue,
  setInputValue,
  setLoading,
  selectedList,
  selected,
  onSelect,
  setButtonType,
  buttonType,
  INITIAL_VALUE,
  onHandleCouponUserUpdate,
  modalData,
  setModalData,
  setSelectedList,
}) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const [summary, setSummary] = React.useState(SUMMARY_VALUE);
  const [filterList, setFilterList] = React.useState(coupon?.user);
  const [currentCouponData, setCurrentCouponData] = React.useState(null);
  const [isOpen, setIsOpen] = React.useState(false);
  /* ===== VARIABLE ===== */
  const styles = {
    title: {
      fontSize: 24,
      fontWeight: 700,
      marginBottom: 20,
    },
    button: {
      width: 80,
      height: 40,
      fontSize: 15,
      fontWeight: 600,
      margin: '0px 4px',
    },
    form: {
      input: {
        large: {
          width: 175,
          marginRight: 8,
        },
        medium: {
          width: 100,
          marginRight: 8,
        },
      },
      button: {
        marginRight: 8,
      },
      date: {
        width: 120,
      },
      text: {
        fontSize: 13,
        fontWeight: 400,
      },

      labelStyle: {
        // width: 155,
        width: '18%',
        color: '#111',
        fontWeight: '500',
        background: '#f4f4f4',
      },
      contentStyle: { width: '32%' },
      defaultStyle: {
        borderTop: '2px solid #d1d1d1',
        borderRadius: 5,
        marginTop: 5,
      },
    },
  };

  const formItem = [
    {
      label: '회원 유형',
      form: [
        {
          input: (
            <Radio.Group
              value={inputValue.type}
              onChange={(e) => onChange('type', e.target.value)}
            >
              <Radio value={''}>전체</Radio>
              <Radio value={'MEMBER'}>회원</Radio>
              <Radio value={'NONE'}>비회원</Radio>
            </Radio.Group>
          ),
        },
      ],
    },
    {
      label: '검색어',
      form: [
        {
          input: (
            <Select
              value={inputValue?.searchKey}
              options={[
                { label: '아이디', value: 'user_account' },
                { label: '회원명', value: 'user_name' },
                { label: '전화번호', value: 'user_phone' },
              ]}
              style={{ ...styles.form.input.large }}
              onChange={(e) => onChange('searchKey', e)}
            />
          ),
        },
        {
          input: (
            <Input
              placeholder="검색어를 입력해주세요"
              style={{ ...styles.form.input.large }}
              value={inputValue?.searchValue}
              onChange={(e) => onChange('searchValue', e.target.value)}
            />
          ),
        },
      ],
    },
    {
      label: '발급일',
      form: [
        {
          input: (
            <Flex gap="large" size="large">
              <Radio.Group
                size="large"
                value={inputValue.value}
                onChange={(e) => {
                  onChange('value', e.target.value);
                  onChange(
                    'start_date',
                    dayjs().subtract(e.target.value, 'day').startOf('day')
                  );
                  onChange('end_date', dayjs().endOf('day'));
                }}
              >
                <Radio.Button value={1}>1일</Radio.Button>
                <Radio.Button value={7}>7일</Radio.Button>
                <Radio.Button value={30}>30일</Radio.Button>
                <Radio.Button value={90}>90일</Radio.Button>
              </Radio.Group>
              <Flex align="center" gap={4}>
                <DatePicker
                  size="large"
                  format={'YYYY-MM-DD'}
                  placeholder=""
                  style={{ ...styles.form.date, width: 150 }}
                  value={dayjs(inputValue.start_date)}
                  onChange={(e) =>
                    onChange('start_date', dayjs(e).startOf('day'))
                  }
                  maxDate={inputValue.end_date}
                />
                <p style={{ ...styles.form.text, padding: '0px 2px' }}>~</p>
                <DatePicker
                  size="large"
                  format={'YYYY-MM-DD'}
                  placeholder=""
                  style={{ ...styles.form.date, width: 150 }}
                  value={dayjs(inputValue.end_date)}
                  onChange={(e) => onChange('end_date', dayjs(e).endOf('day'))}
                  minDate={inputValue.start_date}
                />
              </Flex>
            </Flex>
          ),
        },
      ],
    },
  ];

  const col = [
    {
      title: '회원번호',
      dataIndex: 'user_id',
      key: 'user_id',
      width: 50,
      sorter: {
        compare: (a, b) => a.user_id - b.user_id,
        multiple: 2,
      },
      render: (user_id) => <>{user_id}</>,
    },
    {
      title: '회원구분',
      dataIndex: 'user_type',
      key: 'user_type',
      width: 50,
      filters: [
        { text: '회원', value: 'USER' },
        { text: '비회원', value: 'NONE' },
      ],
      onFilter: (value, record) => record.user_type.includes(value),
      render: (user_type) => (
        <Tag {...{ color: TYPE_VAlUE[user_type]?.color }}>
          {TYPE_VAlUE[user_type]?.label}
        </Tag>
      ),
    },
    {
      title: '휴대폰번호',
      dataIndex: 'user_phone',
      key: 'user_phone',
      width: 100,
      render: (user_phone) => <>{user_phone}</>,
    },
    {
      title: '이름(아이디)',
      dataIndex: 'user_name',
      key: 'user_name',
      width: 100,
      render: (user_name, { user_type, user_id, user_account }) => {
        return user_type === 'USER' ? (
          <>
            <Avatar style={{ marginRight: 5 }}>{user_name}</Avatar>
            <Link
              to={`/users/list?modal=${encodeURIComponent(encrypt(user_id))}`}
            >
              {strCut(user_name, 10)}({strCut(user_account, 10)})
            </Link>
          </>
        ) : (
          <>{'-'}</>
        );
      },
    },
    {
      title: '발급수량',
      dataIndex: 'coupon_count',
      key: 'coupon_count',
      width: 50,
      render: (_, { coupon_user }) => <>{coupon_user?.length} 개</>,
      sorter: {
        compare: (a, b) => a.coupon_user?.length - b.coupon_user?.length,
        multiple: 2,
      },
    },
    {
      title: '사용수량',
      dataIndex: 'used_coupon_count',
      key: 'used_coupon_count',
      width: 50,
      render: (_, { coupon_user }) => (
        <>{calculateCoupon('USE_COUNT', coupon_user)} 개</>
      ),
      sorter: {
        compare: (a, b) =>
          calculateCoupon('USE_COUNT', a.coupon_user) -
          calculateCoupon('USE_COUNT', b.coupon_user),
        multiple: 2,
      },
    },
    {
      title: '미사용수량',
      dataIndex: 'not_used_coupon_count',
      key: 'not_used_coupon_count',
      width: 50,
      render: (_, { coupon_user }) => (
        <>{calculateCoupon('NOT_USED_COUPON_COUNT', coupon_user)} 개</>
      ),
      sorter: {
        compare: (a, b) =>
          calculateCoupon('NOT_USED_COUPON_COUNT', a.coupon_user) -
          calculateCoupon('NOT_USED_COUPON_COUNT', b.coupon_user),
        multiple: 2,
      },
    },
    {
      title: '만료수량',
      dataIndex: 'expired_coupon_count',
      key: 'expired_coupon_count',
      width: 50,
      render: (_, { coupon_user }) => (
        <>{calculateCoupon('EXPIRED_COUPON', coupon_user)} 개</>
      ),
      sorter: {
        compare: (a, b) =>
          calculateCoupon('EXPIRED_COUPON', a.coupon_user) -
          calculateCoupon('EXPIRED_COUPON', b.coupon_user),
        multiple: 2,
      },
    },
    {
      title: (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <p>만료 예정 쿠폰</p>
          <p>(7일 기준)</p>
        </div>
      ),
      dataIndex: 'expired_7ago_coupon_count',
      key: 'expired_7ago_coupon_count',
      width: 50,
      render: (_, { coupon_user }) => (
        <>{calculateCoupon('7AGO_EXPIRED_COUPON', coupon_user)} 개</>
      ),
      sorter: {
        compare: (a, b) =>
          calculateCoupon('7AGO_EXPIRED_COUPON', a.coupon_user) -
          calculateCoupon('7AGO_EXPIRED_COUPON', b.coupon_user),
        multiple: 2,
      },
    },
  ];

  const modalCol = [
    {
      title: '쿠폰번호',
      dataIndex: 'coupon_id',
      key: 'coupon_id',
      width: 100,
      fixed: 'left',
    },
    {
      title: '쿠폰함번호',
      dataIndex: 'coupon_user_id',
      key: 'coupon_user_id',
      width: 150,
      sorter: {
        compare: (a, b) => a?.coupon_user_id - b?.coupon_user_id,
        multiple: 2,
      },
    },
    {
      title: '쿠폰코드',
      dataIndex: 'serial_number',
      key: 'serial_number',
      width: 200,
    },
    {
      title: '쿠폰명',
      dataIndex: 'coupon_name',
      key: 'coupon_name',
      width: 200,
      render: (_, { policy_coupon }) => <>{policy_coupon?.coupon_name}</>,
    },
    {
      title: '발급시점 쿠폰내역',
      dataIndex: 'policy_coupon',
      key: 'policy_coupon',
      width: 300,
      render: (policy_coupon, _item) => (
        <Flex vertical="true" security="flex_start">
          <Text>쿠폰 명 : {policy_coupon?.coupon_name}</Text>
          <Text>
            할인 : {policy_coupon?.discount_rate}{' '}
            {TYPE_VAlUE[policy_coupon?.discount_type]?.label}
          </Text>
          <Text>
            적립 : {policy_coupon?.reserve_rate}{' '}
            {TYPE_VAlUE[policy_coupon?.reserve_type]?.label}
          </Text>
          <Text>쿠폰 타입 : {policy_coupon?.coupon_type}</Text>
          <Text>
            발급 타입 : {VOUCHER_TYPE[policy_coupon?.voucher_type]?.label}
          </Text>
          <Text>수수료율 : {policy_coupon?.commission_rate} %</Text>
          <Text>분담비율 : {policy_coupon?.share_ratio} %</Text>
        </Flex>
      ),
    },
    {
      title: '발급일자',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 200,
      sorter: {
        compare: (a, b) => a.created_at - b.created_at,
        multiple: 2,
      },
      render: (created_at) =>
        dayjs(created_at * 1000).format('YYYY-MM-DD(HH:mm)'),
    },
    {
      title: '사용일자',
      dataIndex: 'use_date',
      key: 'use_date',
      width: 200,
      sorter: {
        compare: (a, b) => a.use_date - b.use_date,
        multiple: 2,
      },
      render: (use_date) => {
        return use_date
          ? dayjs(use_date * 1000).format('YYYY-MM-DD(HH:mm)')
          : '-';
      },
    },
    {
      title: '만료일자',
      dataIndex: 'expired_date',
      key: 'expired_date',
      width: 200,
      sorter: {
        compare: (a, b) => a.expired_date - b.expired_date,
        multiple: 2,
      },
      render: (expired_date) => {
        return expired_date
          ? dayjs(expired_date * 1000).format('YYYY-MM-DD(HH:mm)')
          : '-';
      },
    },
  ];

  /* ===== FUNCTION ===== */
  /* ===== HOOKS ===== */
  /* ===== RENDER ===== */
  /**
   * 쿠폰 계산
   * --
   * @param {*} type
   * @returns
   */
  const calculateCoupon = (type, value = null) => {
    const nowDateUnix = dayjs().unix();
    const user = coupon?.user || [];
    switch (type) {
      case 'COUPON_COUNT':
        return user?.reduce((acc, user) => {
          return acc + user?.coupon_user?.length;
        }, 0);

      case 'NOT_USED_COUPON_COUNT':
        return !value
          ? user?.reduce((acc, user) => {
              //토탈
              return (
                acc +
                user?.coupon_user?.filter((coupon) =>
                  !coupon?.use_date && nowDateUnix < coupon?.expired_date
                    ? true
                    : false
                )?.length
              );
            }, 0)
          : //회원
            value?.filter((coupon) =>
              !coupon?.use_date && nowDateUnix < coupon?.expired_date
                ? true
                : false
            )?.length;

      case 'USE_COUNT':
        return !value
          ? user?.reduce((acc, user) => {
              //토탈
              return (
                acc +
                user?.coupon_user?.filter((coupon) =>
                  coupon?.use_date ? true : false
                )?.length
              );
            }, 0)
          : //회원
            value?.filter((coupon) => (coupon?.use_date ? true : false))
              ?.length;

      //사용날짜가 없으며, 만료된 쿠폰
      case 'EXPIRED_COUPON':
        return !value
          ? user?.reduce((acc, user) => {
              //토탈
              return (
                acc +
                user?.coupon_user?.filter(
                  (coupon) =>
                    !coupon?.use_date && nowDateUnix > coupon?.expired_date
                )?.length
              );
            }, 0)
          : //회원
            value?.filter(
              (coupon) =>
                !coupon?.use_date && nowDateUnix > coupon?.expired_date
            )?.length;

      //사용날짜가 없으며, 7일뒤 만료되는 쿠폰
      case '7AGO_EXPIRED_COUPON':
        return !value
          ? user?.reduce((acc, user) => {
              //토탈
              return (
                acc +
                user?.coupon_user?.filter((coupon) => {
                  const expDay = dayjs
                    .unix(coupon?.expired_date)
                    .diff(dayjs.unix(EXPIRY_DATE), 'day');
                  if (
                    !coupon?.use_date &&
                    nowDateUnix < coupon?.expired_date &&
                    expDay >= 0 &&
                    expDay <= EXPIRY
                  ) {
                    return true;
                  }
                  return false;
                })?.length
              );
            }, 0)
          : //회원
            value?.filter((coupon) => {
              const expDay = dayjs
                .unix(coupon?.expired_date)
                .diff(dayjs.unix(EXPIRY_DATE), 'day');
              if (
                !coupon?.use_date &&
                nowDateUnix < coupon?.expired_date &&
                expDay >= 0 &&
                expDay <= EXPIRY
              ) {
                return true;
              }
              return false;
            })?.length;

      default:
        return 0;
    }
  };

  /* ===== FUNCTION ===== */
  /**
   * 포인트 조건 검색
   * --
   * @param {string} t
   */

  const handleSearch = (t) => {
    setLoading(true);
    const searchType = t.toLocaleUpperCase();
    let arr = coupon?.user;
    switch (searchType) {
      case 'SEARCH':
        const { type, searchKey, searchValue } = inputValue;

        if (type === '') {
          arr = arr.map((user) => user);
        }
        //
        if (type === 'MEMBER') {
          arr = arr.filter((user) => (user.user_name ? true : false));
        }
        //
        if (type === 'NONE') {
          arr = arr.filter((user) => (!user.user_name ? true : false));
        }
        arr =
          searchKey && searchValue
            ? handleSearchKeyword(arr, searchValue, searchKey)
            : arr;

        break;

      default:
        break;
    }
    const sort = arr.sort((a, b) => a.created_at - b.created_at);
    setSummary((prev) => ({
      ...prev,
      coupon_count: sort?.reduce((acc, user) => {
        return acc + user?.coupon_user?.length;
      }, 0),
    }));
    setFilterList(sort);
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };
  /**
   * 엑셀 다운
   * ---
   * @returns
   *
   */
  const handleXlsx = () => {
    try {
      let cols = [];
      let rows = filterList;
      if (buttonType === 'SUMMARY') {
        cols = col;
        cols.splice(8, 1, {
          title: '만료 예정 쿠폰(7일 기준)',
          dataIndex: 'expired_7ago_coupon_count',
          key: 'expired_7ago_coupon_count',
        });
        cols.splice(3, 1, {
          title: '이름',
          dataIndex: 'user_name',
          key: 'user_name',
        });
        cols.splice(4, 0, {
          title: '아이디',
          dataIndex: 'user_account',
          key: 'user_account',
        });
        rows = filterList
          .map((user) => {
            return user?.coupon_user?.map((coupon) => {
              return {
                ...coupon,
                user_phone: user?.user_phone,
                user_name: user?.user_name,
                user_account: user?.user_account,
                user_type: TYPE_VAlUE[user?.user_type]?.label,
                coupon_count: user?.coupon_user?.length,
                used_coupon_count: calculateCoupon(
                  'USE_COUNT',
                  user?.coupon_user
                ),
                not_used_coupon_count: calculateCoupon(
                  'NOT_USED_COUPON_COUNT',
                  user?.coupon_user
                ),
                expired_coupon_count: calculateCoupon(
                  'EXPIRED_COUPON',
                  user?.coupon_user
                ),
                expired_7ago_coupon_count: calculateCoupon(
                  'EXPIRED_COUPON',
                  user?.coupon_user
                ),
              };
            });
          })
          .flat();
        return {
          columns: cols,
          rows: rows,
          file_name: '쿠폰요약내역',
        };
      }
      if (buttonType === 'COUPON_DETAIL') {
        cols = [
          {
            title: '회원번호',
            dataIndex: 'user_id',
            key: 'user_id',
          },
          {
            title: '회원구분',
            dataIndex: 'user_type',
            key: 'user_type',
          },
          {
            title: '휴대폰번호',
            dataIndex: 'user_phone',
            key: 'user_phone',
          },
          {
            title: '이름',
            dataIndex: 'user_name',
            key: 'user_name',
          },
          {
            title: '아이디',
            dataIndex: 'user_account',
            key: 'user_account',
          },
          {
            title: '쿠폰번호',
            dataIndex: 'coupon_id',
            key: 'coupon_id',
          },
          {
            title: '쿠폰함번호',
            dataIndex: 'coupon_user_id',
            key: 'coupon_user_id',
          },
          {
            title: '쿠폰코드',
            dataIndex: 'serial_number',
            key: 'serial_number',
          },
          {
            title: '쿠폰명(지급당시)',
            dataIndex: 'ago_coupon_name',
            key: 'ago_coupon_name',
          },
          {
            title: '할인(지급당시)',
            dataIndex: 'discount_rate',
            key: 'discount_rate',
          },
          {
            title: '적립(지급당시)',
            dataIndex: 'reserve_rate',
            key: 'reserve_rate',
          },
          {
            title: '쿠폰타입(지급당시)',
            dataIndex: 'coupon_type',
            key: 'coupon_type',
          },
          {
            title: '발급타입(지급당시)',
            dataIndex: 'voucher_type',
            key: 'voucher_type',
          },
          {
            title: '수수료율(지급당시)',
            dataIndex: 'commission_rate',
            key: 'commission_rate',
          },
          {
            title: '분담비율(지급당시)',
            dataIndex: 'share_ratio',
            key: 'share_ratio',
          },
          {
            title: '발급일자',
            dataIndex: 'created_at',
            key: 'created_at',
          },
          {
            title: '사용일자',
            dataIndex: 'use_date',
            key: 'use_date',
          },
          {
            title: '만료일자',
            dataIndex: 'expired_date',
            key: 'expired_date',
          },
        ];
        rows = filterList
          ?.map((user) => {
            return user?.coupon_user?.map((coupon) => {
              return {
                ...user,
                ...coupon,
                serial_number: coupon?.serial_number,
                user_type: TYPE_VAlUE[user?.user_type]?.label,
                ago_coupon_name: coupon?.policy_coupon?.coupon_name,
                discount_rate: `${coupon?.policy_coupon?.discount_rate} ${
                  TYPE_VAlUE[coupon?.policy_coupon?.discount_type]?.label
                }`,
                reserve_rate: `${coupon?.policy_coupon?.reserve_rate} ${
                  TYPE_VAlUE[coupon?.policy_coupon?.reserve_type]?.label
                }`,
                coupon_type: coupon?.policy_coupon?.coupon_type,
                voucher_type:
                  VOUCHER_TYPE[coupon?.policy_coupon?.voucher_type]?.label,
                commission_rate: coupon?.policy_coupon?.commission_rate,
                share_ratio: coupon?.policy_coupon?.share_ratio,
              };
            });
          })
          .flat();
        return {
          columns: cols,
          rows: rows,
          file_name: '회원쿠폰상세내역',
        };
      }
    } catch (e) {
      MessageAlert.error('엑셀 다운로드 싪패');
      setLoading(false);
    } finally {
      setTimeout(() => setLoading(false), 1000);
    }
  };

  /* ===== Hooks ===== */

  React.useEffect(() => {
    setFilterList(coupon?.user);
  }, [currentCoupon]);

  React.useEffect(() => {
    setSummary((prev) => ({
      ...prev,
      not_used_coupon_count: stringToMoneyFormat(
        calculateCoupon('NOT_USED_COUPON_COUNT')
      ),
      used_coupon_count: stringToMoneyFormat(calculateCoupon('USE_COUNT')),
      expired_coupon_count: stringToMoneyFormat(
        calculateCoupon('EXPIRED_COUPON')
      ),
      expired_7ago_coupon_count: stringToMoneyFormat(
        calculateCoupon('7AGO_EXPIRED_COUPON')
      ),
    }));
  }, [currentCoupon, coupon]);

  React.useEffect(() => {
    handleSearch('search');
  }, [couponUserList, couponUserList]);

  return (
    <>
      {/* === Loading - start === */}
      <Spin spinning={loading} indicator={<LoadingOutlined spin />}>
        {/* ===== 헤더&필터 - start ===== */}
        <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
          {/* === 타이틀 === */}
          <Title style={{ ...styles.title }}>쿠폰 발급 내역</Title>

          {/* === 필터 - start === */}
          <ExpandableContainer borderRadius={4} padding={20}>
            {formItem.map((_item, _index) => (
              <div
                key={`form_${_index}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '10px 0px',
                }}
              >
                <div style={{ width: 120, fontSize: 13, fontWeight: 400 }}>
                  <label>{_item.label}</label>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {_item.form.map((_subitem, _subkey) => (
                    <React.Fragment key={`input_${_subkey}`}>
                      {_subitem.input}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ))}
          </ExpandableContainer>

          {/* 검색/초기화 버튼 */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              marginTop: 13,
            }}
          >
            <Button
              size="large"
              type="primary"
              style={{
                ...styles.button,
                background: 'var(--primary-color)',
              }}
              onClick={() => {
                setPeriod({
                  start_date: inputValue.start_date.unix(),
                  end_date: inputValue.end_date.unix(),
                });
                handleSearch('search');
              }}
            >
              검색
            </Button>
            <Button
              size="large"
              style={{ ...styles.button }}
              onClick={() => {
                setPeriod({
                  start_date: inputValue.start_date.unix(),
                  end_date: inputValue.end_date.unix(),
                });
                setInputValue(INITIAL_VALUE);
                handleSearch('reset');
              }}
            >
              초기화
            </Button>
          </div>
          {/* 검색/초기화 버튼 끝 */}
          {/* === 필터 - end === */}
        </Content>
        {/* ===== 헤더&필터 - end ===== */}

        <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
          {/* === 통계 - start === */}
          <Content
            maxWidth={'100%'}
            padding={0}
            backgroundColor={'none'}
            style={{
              marginBottom: 4,
              display: 'flex',
              gap: '2px',
            }}
          >
            {[
              {
                title: '발급수량',
                value: summary.coupon_count,
                suffix: '개',
              },
              {
                title: '사용수량',
                value: summary.used_coupon_count,
                suffix: '개',
              },
              {
                title: '미사용수량',
                value: summary.not_used_coupon_count,
                suffix: '개',
              },
              {
                title: '만료수량',
                value: summary.expired_coupon_count,
                suffix: '개',
              },
              {
                title: '만료 예정 쿠폰(7일 기준)',
                value: summary.expired_7ago_coupon_count,
                suffix: '개',
              },
            ].map((_item, _index) => (
              <div
                key={`${_item.label}-${_index}`}
                className="boxShadow"
                style={{
                  background: '#FFFFFF',
                  borderRadius: 4,
                  width: '100%',
                  margin: '2px',
                  padding: '26px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <Statistic
                  style={{ justifyContent: 'center' }}
                  title={_item.title}
                  value={_item.value}
                  suffix={_item.suffix}
                  direction={'row'}
                />
              </div>
            ))}
          </Content>
          {/* === 통계 - end === */}

          {/* ===== 하단컨텐츠 - start ===== */}
          <Row gutter={[1, 1]} className="boxShadow">
            {/* === 회원목록 - start === */}
            {permission === 'ADMIN' && (
              <Col x={4}>
                <CouponList
                  coupons={couponList}
                  onChange={setCurrentCoupon}
                  fetch={false}
                  // query={{ filter: 'user_name' }}
                  maxHeight={611}
                />
              </Col>
            )}
            {/* === 회원목록 - end === */}

            {/* === 리뷰목록 - start === */}
            <Col x={permission === 'ADMIN' ? 20 : 24}>
              <Content
                maxWidth={'100%'}
                width={1400}
                height={'100%'}
                minHeight={490}
                maxHeight={662}
                borderRadius={4}
                padding={20}
              >
                {/* === 헤더 === */}
                <div>
                  <Title
                    size={4}
                    style={{ marginBottom: 10 }}
                    subButton={
                      <Flex gap={4}>
                        <Button
                          disabled={!currentCoupon && true}
                          onClick={() => {
                            setButtonType('SUMMARY');
                            setLoading(true);
                            setTimeout(() => {
                              const { columns, rows, file_name } = handleXlsx();
                              downloadXlsx({
                                columns: columns,
                                rows: rows,
                                fileName: file_name,
                              });
                            }, 1000);
                          }}
                        >
                          엑셀로 다운로드
                        </Button>
                        <Button
                          disabled={!currentCoupon && true}
                          onClick={() => {
                            setButtonType('COUPON_DETAIL');
                            setLoading(true);
                            setTimeout(() => {
                              const { columns, rows, file_name } = handleXlsx();
                              downloadXlsx({
                                columns: columns,
                                rows: rows,
                                fileName: file_name,
                              });
                            }, 1000);
                          }}
                        >
                          상세내역 엑셀로 다운로드
                        </Button>
                      </Flex>
                    }
                  >
                    <span>
                      검색결과 총 회원 <a>{filterList?.length || 0}</a> 명
                    </span>
                  </Title>
                </div>

                {/* === 포인트 목록 === */}

                <Table
                  size="small"
                  pagination={{
                    pageSize: 50,
                  }}
                  scroll={{
                    x: 1250,
                    y: 49 * 10,
                  }}
                  dataSource={filterList}
                  columns={col}
                  onRow={(data) => {
                    return {
                      onClick: () => {
                        setModalData(data);
                      },
                    };
                  }}
                />
              </Content>
            </Col>
          </Row>
          {/* ===== 하단컨텐츠 - end ===== */}
        </Content>
      </Spin>
      {/* === Loading - start === */}
      {/* NOTE: 쿠폰 상세리스트 모달 */}
      <ModalLayout
        type={'drawer'}
        open={modalData}
        onCancel={() => {
          setSelectedList([]);
          onChange('update_date', '');
          setModalData(null);
        }}
        title={'회원 쿠폰 상세 정보'}
        width={'100%'}
        maxWidth={1300}
      >
        <Content maxWidth={'100%'} padding={0}>
          <Row>
            {/* === 모달 > 상세보기: start === */}
            <Col x={24}>
              <Title size={5} style={{ marginBottom: 5 }}>
                회원 정보
              </Title>
              {modalData ? (
                <AntDescriptions
                  bordered
                  column={2}
                  size="small"
                  labelStyle={styles.labelStyle}
                  contentStyle={styles.contentStyle}
                  style={styles.defaultStyle}
                  items={[
                    {
                      key: '1',
                      label: '회원번호',
                      children: `${modalData?.user_id}`,
                    },
                    {
                      key: '2',
                      label: '회원명',
                      children: `${modalData?.user_name ?? '-'}`,
                    },
                    {
                      key: '3',
                      label: '회원 아이디',
                      children: `${modalData?.user_account ?? '-'}`,
                    },
                    {
                      key: '4',
                      label: '계정상태',
                      children: (
                        <Tag
                          color={
                            ACCOUNT_STATUS[modalData?.account_status]?.color
                          }
                        >
                          {ACCOUNT_STATUS[modalData?.account_status]?.label}
                        </Tag>
                      ),
                    },
                    {
                      key: '5',
                      label: '연락처',
                      children: `${modalData?.user_phone ?? '-'}`,
                    },
                    {
                      key: '6',
                      label: '이메일',
                      children: `${modalData?.user_email ?? '-'}`,
                    },
                    {
                      key: '7',
                      label: '성별',
                      children: `${
                        modalData?.user_gender === 'M' ? '남' : '여'
                      }`,
                    },
                    {
                      key: '8',
                      label: '생년월일',
                      children: `${Formatter.unixToDate(
                        modalData?.user_birthday,
                        'YYYY.MM.DD'
                      )}`,
                    },
                    {
                      key: '9',
                      label: '주소',
                      children: (
                        <Flex justify="space-between" align="center">
                          <Text size={14} maxLine={2}>{`(${
                            modalData?.postcode
                          }) ${modalData?.user_address ?? '-'}\n${
                            modalData?.user_address_detail ?? ''
                          }`}</Text>
                        </Flex>
                      ),
                      span: 2,
                    },
                    {
                      key: '10',
                      label: '닉네임',
                      children: `${modalData?.user_nick_name ?? '-'}`,
                    },
                    {
                      key: '11',
                      label: '멤버십 번호',
                      children: `${
                        Formatter.membership(modalData?.membership_code) ?? '-'
                      }`,
                    },
                    {
                      key: '12',
                      label: '가입일',
                      children: `${Formatter.unixToDate(
                        modalData?.created_at,
                        'YYYY.MM.DD HH:mm:ss'
                      )}`,
                    },
                    {
                      key: '13',
                      label: '최근 접속일',
                      children: `${Formatter.unixToDate(
                        modalData?.access_time,
                        'YYYY.MM.DD HH:mm:ss'
                      )}`,
                    },
                  ]}
                />
              ) : null}
              {/* === 모달 > 상세보기: end === */}
              {/* === 모달 > 상세보기 > 회원쿠폰요약: start === */}
              <Content
                maxWidth={'100%'}
                padding={0}
                backgroundColor={'none'}
                style={{
                  margin: '10px 0px',
                  display: 'flex',
                  gap: '2px',
                  // border: '1px solid black',
                }}
              >
                {[
                  {
                    title: '발급수량',
                    value: modalData?.coupon_user?.length,
                    suffix: '개',
                  },
                  {
                    title: '사용수량',
                    value: (
                      <>
                        {calculateCoupon('USE_COUNT', modalData?.coupon_user)}
                      </>
                    ),
                    suffix: '개',
                  },
                  {
                    title: '미사용수량',
                    value: (
                      <>
                        {calculateCoupon(
                          'NOT_USED_COUPON_COUNT',
                          modalData?.coupon_user
                        )}
                      </>
                    ),
                    suffix: '개',
                  },
                  {
                    title: '만료수량',
                    value: (
                      <>
                        {calculateCoupon(
                          'EXPIRED_COUPON',
                          modalData?.coupon_user
                        )}
                      </>
                    ),
                    suffix: '개',
                  },
                  {
                    title: '만료 예정 쿠폰(7일 기준)',
                    value: (
                      <>
                        {calculateCoupon(
                          '7AGO_EXPIRED_COUPON',
                          modalData?.coupon_user
                        )}
                      </>
                    ),
                    suffix: '개',
                  },
                ].map((_item, _index) => (
                  <div
                    key={`${_item.label}-${_index}`}
                    className="boxShadow"
                    style={{
                      background: '#FFFFFF',
                      borderRadius: 4,
                      width: '100%',
                      margin: '2px',
                      padding: '26px 14px',
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      border: '0.2px solid #666666',
                    }}
                  >
                    <Statistic
                      style={{
                        justifyContent: 'center',
                      }}
                      title={_item.title}
                      value={_item.value}
                      suffix={_item.suffix}
                      direction={'row'}
                    />
                  </div>
                ))}
              </Content>
              {/* === 모달 > 상세보기 > 회원쿠폰요약: end === */}
              <Flex justify="space-between" style={{ margin: '10px 0' }}>
                <Title size={5} style={{ margin: '10px 0px' }}>
                  회원 쿠폰 리스트
                </Title>
                <Flex gap={10}>
                  <Button
                    disabled={selectedList?.length > 0 ? false : true}
                    type="primary"
                    size="large"
                    onClick={() => {
                      setIsOpen(!isOpen);
                      setButtonType('expired_date');
                    }}
                  >
                    {selectedList?.length}건 만료 처리
                  </Button>
                  <Button
                    disabled={selectedList?.length > 0 ? false : true}
                    type="primary"
                    size="large"
                    onClick={() => {
                      setIsOpen(!isOpen);
                      setButtonType('use_date');
                    }}
                  >
                    {selectedList?.length}건 시용 처리
                  </Button>
                </Flex>
              </Flex>
              {/* === 모달 > 상세보기 > 회원쿠폰리스트: start === */}
              <Row>
                <Table
                  rowSelection={{
                    columnWidth: 50, // 체크박스 열 너비 조정
                    size: 'large',
                    type: 'checkbox',
                    onSelect: (record, selected, selectedRows, nativeEvent) => {
                      onSelect(selected ? 'checked' : 'unchecked', record);
                    },
                    onSelectAll: (selected, selectedRows, changeRows) => {
                      onSelect(
                        selected ? 'all-checked' : 'all-unchecked',
                        selectedRows
                      );
                    },
                    selectedRowKeys: selected?.map((_item) => _item?.key),
                  }}
                  size="large"
                  pagination={{
                    pageSize: 50,
                  }}
                  scroll={{
                    x: 1250,
                    y: 49 * 10,
                  }}
                  dataSource={modalData?.coupon_user}
                  columns={modalCol}
                />
              </Row>
              {/* === 모달 > 상세보기 > 회원쿠폰리스트: end === */}
            </Col>
          </Row>
        </Content>
      </ModalLayout>

      {/* NOTE: 쿠폰 수정 모달 */}
      <ModalLayout
        open={isOpen}
        onCancel={() => {
          setSelectedList([]);
          onChange('update_date', '');
          setIsOpen(!isOpen);
        }}
        width={300}
        centered
      >
        <Content maxWidth={'100%'} padding={0}>
          <Flex vertical="coulnms" justify="center" gap={10}>
            <DatePicker
              size="large"
              showTime={true}
              style={{ width: '100%' }}
              value={
                inputValue.update_date ? dayjs(inputValue.update_date) : null
              }
              onChange={(e) => {
                onChange('update_date', e);
              }}
              placeholder="날짜 선택"
              format="YYYY-MM-DD HH시 mm분"
            />
            <Button
              type="primary"
              danger={buttonType === 'USED' ? false : true}
              onClick={() => {
                onHandleCouponUserUpdate();
                setTimeout(() => {
                  setIsOpen(false);
                  onChange('update_date', '');
                  setButtonType(null);
                }, 1000);
              }}
            >
              {buttonType === 'use_date' ? '시용처리 하기' : '만료처리 하기'}
            </Button>
          </Flex>
        </Content>
      </ModalLayout>
    </>
  );
};
UserCouponListPresenter.defaultProps = {
  users: [],
};
export default UserCouponListPresenter;
